import FormProvider, { RHFTextField } from '@common/components/lib/react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useAuth, { CheckEmailResponse } from '@modules/auth/hooks/api/useAuth';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Warning from '@mui/icons-material/Warning';
import Facebook from '@mui/icons-material/Facebook';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoginOutlined from '@mui/icons-material/LoginOutlined';
import {
  Grid,
  Typography,
  Box,
  Divider,
  IconButton,
  InputAdornment,
  Fade,
  Stack,
  alpha,
  useTheme,
  Slide,
  Button,
  Alert,
} from '@mui/material';
import { useForm, FieldValues } from 'react-hook-form';
import * as Yup from 'yup';
import Routes from '@common/defs/routes';
import { useState, useMemo, useEffect } from 'react';
import { useRouter } from 'next/router';
import AuthLayout from '@modules/auth/components/layouts/AuthLayout';
import AuthCard from '@modules/auth/components/common/AuthCard';
import StyledLoadingButton from '@modules/auth/components/common/StyledLoadingButton';
import SocialAuthButton from '@modules/auth/components/common/SocialAuthButton';
import GoogleIcon from '@common/assets/svgs/GoogleIcon';
import { User } from '@modules/users/defs/types';
import LoadingScreen from '@common/layout/LoadingScreen';
import { useTracking } from '@common/hooks/useTracking';
import { safeLocalStorage, isFacebookWithBrokenStorage } from '@common/utils/storage';
import { useAuthFlow } from '@modules/auth/contexts/AuthFlowContext';

const SignupForm = () => {
  const { login, register, checkEmail, GetAuthUrl } = useAuth();
  const theme = useTheme();
  const router = useRouter();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailChecked, setEmailChecked] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [currentEmail, setCurrentEmail] = useState('');
  const [transitioning, setTransitioning] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [emailCheckResult, setEmailCheckResult] = useState<CheckEmailResponse | null>(null);
  const [showSocialOnlyMessage, setShowSocialOnlyMessage] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const { trackEvent } = useTracking();
  const { setAuthFlowData, consumeAuthFlowData } = useAuthFlow();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fbId = urlParams.get('fbid');
    const refUserId = urlParams.get('ref');

    if (fbId) {
      safeLocalStorage.setItem('auth_fbid', fbId);
    }
    if (refUserId) {
      safeLocalStorage.setItem('auth_ref', refUserId);
    }
    const lid = urlParams.get('lid');
    if (lid) {
      safeLocalStorage.setItem('auth_lid', lid);
    }
    const practiceId = urlParams.get('practice-id');
    if (practiceId) {
      safeLocalStorage.setItem('auth_practice_id', practiceId);
    }

    const params: Record<string, string> = {};
    urlParams.forEach((value, key) => {
      params[key] = value;
    });
    if (Object.keys(params).length > 0) {
      safeLocalStorage.setItem('signup_url_parameters', JSON.stringify(params));
    }

    const flowData = consumeAuthFlowData();
    if (flowData) {
      setValue('email', flowData.email);
      setCurrentEmail(flowData.email);
      setEmailCheckResult(flowData.checkResult);

      const isSocialOnly =
        flowData.checkResult.exists &&
        !flowData.checkResult.hasPasswordLogin &&
        !flowData.checkResult.hasEmailProvider &&
        (flowData.checkResult.hasGoogleProvider || flowData.checkResult.hasFacebookProvider);

      if (isSocialOnly) {
        setShowSocialOnlyMessage(true);
      } else {
        setIsRegistering(!flowData.checkResult.exists);
        setShowSocialOnlyMessage(false);
      }
      setEmailChecked(true);
    }
  }, []); // eslint-disable-line

  const AuthSchema = Yup.object().shape({
    email: Yup.string()
      .email("Le format de l'email est incorrect")
      .required('Le champ est obligatoire')
      .test(
        'local-part-length',
        "Le préfixe de l'email ne doit pas dépasser 30 caractères",
        (value) => !value || value.split('@')[0].length <= 30
      ),
    password:
      emailChecked && !showSocialOnlyMessage
        ? Yup.string()
            .required('Le champ est obligatoire')
            .min(6, 'Le mot de passe doit contenir au moins 6 caractères')
        : Yup.string(),
    confirmPassword:
      emailChecked && isRegistering && !showSocialOnlyMessage
        ? Yup.string()
            .required('Le champ est obligatoire')
            .oneOf([Yup.ref('password')], 'Les mots de passe doivent correspondre')
        : Yup.string(),
  });

  const methods = useForm({
    resolver: yupResolver(AuthSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    trigger,
    setValue,
    reset,
  } = methods;

  const handleSocialAuth = async (provider: 'google' | 'facebook') => {
    try {
      const setLoading = provider === 'google' ? setGoogleLoading : setFacebookLoading;
      setLoading(true);

      safeLocalStorage.setItem('auth_provider', provider);

      const response = await GetAuthUrl(provider, { verbose: false });

      if (response.success && response.data?.redirectUrl) {
        window.location.href = response.data.redirectUrl;
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error(`Error initiating ${provider} OAuth:`, error);
      const setLoading = provider === 'google' ? setGoogleLoading : setFacebookLoading;
      setLoading(false);
    }
  };

  const getMessageFromBackend = (message: string) => {
    return message || '';
  };

  const shouldShowPasswordFields = () => {
    if (!emailChecked || !emailCheckResult) {
      return false;
    }
    if (showSocialOnlyMessage) {
      return false;
    }
    if (!emailCheckResult.exists) {
      return true;
    }
    if (emailCheckResult.exists) {
      return emailCheckResult.hasPasswordLogin || false;
    }
    return true;
  };

  const shouldShowSocialOnlyState = (result: CheckEmailResponse) => {
    return (
      result.exists &&
      !result.hasPasswordLogin &&
      !result.hasEmailProvider &&
      (result.hasGoogleProvider || result.hasFacebookProvider)
    );
  };

  const handleAuthRedirect = (user?: User) => {
    setIsRedirecting(true);

    safeLocalStorage.removeItem('auth_fbid');
    safeLocalStorage.removeItem('auth_ref');
    safeLocalStorage.removeItem('signup_url_parameters');

    if (user?.registrationStep && user.registrationStep !== 'completed') {
      safeLocalStorage.setItem('currentRegistrationStep', user.registrationStep);
      router.replace({ pathname: Routes.Auth.CompleteRegistration, query: router.query });
    } else {
      safeLocalStorage.removeItem('currentRegistrationStep');
      router.replace(Routes.Common.Home);
    }
  };

  const onSubmit = async (data: FieldValues) => {
    if (!emailChecked) {
      const isValidEmail = await trigger('email');
      if (!isValidEmail || !data.email) {
        return;
      }

      setCheckingEmail(true);
      setTransitioning(true);

      try {
        const checkResponse = await checkEmail({ email: data.email }, { verbose: false });

        if (checkResponse.success && checkResponse.data !== undefined) {
          setCurrentEmail(data.email);
          setEmailCheckResult(checkResponse.data);

          await new Promise((resolve) => {
            setTimeout(resolve, 200);
          });

          if (checkResponse.data.exists) {
            setAuthFlowData({
              email: data.email as string,
              checkResult: checkResponse.data,
            });
            router.push({ pathname: Routes.Auth.Login, query: router.query });
            return;
          }

          const isSocialOnly = shouldShowSocialOnlyState(checkResponse.data);

          if (isSocialOnly) {
            setShowSocialOnlyMessage(true);
            setEmailChecked(true);
          } else {
            setIsRegistering(true);
            setEmailChecked(true);
            setShowSocialOnlyMessage(false);
            setValue('password', '');
            setValue('confirmPassword', '');
          }

          setCheckingEmail(false);
          setTimeout(() => {
            setTransitioning(false);
          }, 300);
        } else {
          setCheckingEmail(false);
          setTransitioning(false);
        }
      } catch (error) {
        console.error('Error checking email:', error);
        setCheckingEmail(false);
        setTransitioning(false);
      }
      return;
    }

    if (showSocialOnlyMessage) {
      return;
    }

    if (isRegistering) {
      const fbId = safeLocalStorage.getItem('auth_fbid');
      const refUserId = safeLocalStorage.getItem('auth_ref');
      const signupUrlParametersRaw = safeLocalStorage.getItem('signup_url_parameters');
      const signupUrlParameters = signupUrlParametersRaw
        ? JSON.parse(signupUrlParametersRaw)
        : null;

      const response = await register(
        {
          email: currentEmail,
          password: data.password as string,
          fbId: fbId ?? undefined,
          refUserId: refUserId ? Math.floor(Number(refUserId) / 157) : undefined,
          signup_url_parameters: signupUrlParameters,
        },
        { displayProgress: true, displaySuccess: false }
      );

      if (response.success) {
        const userData = response.data?.user;
        trackEvent('Lead');
        handleAuthRedirect(userData);
      }
    } else {
      const response = await login(
        {
          email: currentEmail,
          password: data.password as string,
        },
        { displayProgress: true, displaySuccess: false }
      );

      if (response.success) {
        const userData = response.data?.user;
        handleAuthRedirect(userData);
      }
    }
  };

  const handleReset = () => {
    setTransitioning(true);

    setTimeout(() => {
      setEmailChecked(false);
      setIsRegistering(false);
      setCurrentEmail('');
      setEmailCheckResult(null);
      setShowSocialOnlyMessage(false);
      reset({
        email: '',
        password: '',
        confirmPassword: '',
      });

      setTimeout(() => {
        setTransitioning(false);
      }, 100);
    }, 150);
  };

  const getTitle = () => {
    if (!emailChecked) {
      return '';
    }
    if (showSocialOnlyMessage) {
      return 'Continuer avec un réseau social';
    }
    return isRegistering ? '' : 'Heureux de vous revoir !';
  };

  const getProviderButtonText = (provider: string) => {
    if (provider === 'google') {
      return 'Google';
    }
    if (provider === 'facebook') {
      return 'Facebook';
    }
    return provider;
  };

  const socialDividerLabel = useMemo(() => {
    if (showSocialOnlyMessage) {
      return 'Méthodes de connexion disponibles';
    }
    return "ou m'inscrire avec";
  }, [showSocialOnlyMessage]);

  const authMessage = useMemo(() => {
    if (showSocialOnlyMessage) {
      return 'Se connecter avec';
    }
    return "M'inscrire avec";
  }, [showSocialOnlyMessage]);

  const renderSocialButtons = () => {
    if (!emailCheckResult) {
      return (
        <>
          <Grid size={{ xs: 12 }}>
            <SocialAuthButton
              icon={<GoogleIcon />}
              label="Google"
              disabled={transitioning || checkingEmail || googleLoading || facebookLoading}
              loading={googleLoading}
              onClick={() => handleSocialAuth('google')}
            />
          </Grid>
        </>
      );
    }

    if (showSocialOnlyMessage && emailCheckResult.availableProviders) {
      return emailCheckResult.availableProviders.map((provider) => (
        <Grid size={{ xs: 12 }} key={provider}>
          <SocialAuthButton
            icon={provider === 'google' ? <GoogleIcon /> : <Facebook sx={{ color: '#1877F2' }} />}
            label={`S'inscrire avec ${getProviderButtonText(provider)}`}
            disabled={transitioning || checkingEmail || googleLoading || facebookLoading}
            loading={provider === 'google' ? googleLoading : facebookLoading}
            onClick={() => handleSocialAuth(provider as 'google' | 'facebook')}
          />
        </Grid>
      ));
    }

    return (
      <>
        <Grid size={{ xs: 12 }}>
          <SocialAuthButton
            icon={<GoogleIcon />}
            label="Google"
            disabled={transitioning || checkingEmail || googleLoading || facebookLoading}
            loading={googleLoading}
            onClick={() => handleSocialAuth('google')}
          />
        </Grid>
      </>
    );
  };

  const shouldShowBlockingMessage = () => {
    if (!emailCheckResult || !emailCheckResult.exists) {
      return false;
    }
    return (
      !emailCheckResult.hasPasswordLogin &&
      (emailCheckResult.hasGoogleProvider || emailCheckResult.hasFacebookProvider) &&
      !emailCheckResult.hasEmailProvider
    );
  };

  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    setIsBlocked(isFacebookWithBrokenStorage());
  }, []);

  if (isBlocked) {
    return (
      <AuthLayout title="Navigateur non supporté">
        <AuthCard>
          <Box
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Warning color="error" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Attention
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              L'application ne peut pas fonctionner correctement dans le navigateur Facebook.
              <br />
              <br />
              Veuillez utiliser votre navigateur habituel (ex: <strong>Chrome</strong> ou{' '}
              <strong>Safari</strong>) pour une meilleure expérience.
            </Typography>
          </Box>
        </AuthCard>
      </AuthLayout>
    );
  }

  if (isRedirecting) {
    return <LoadingScreen />;
  }

  return (
    <AuthLayout title={getTitle()}>
      <AuthCard>
        <Slide direction="down" in={emailChecked} mountOnEnter unmountOnExit>
          <Box
            sx={{
              p: 2,
              background: alpha(theme.palette.primary.main, 0.05),
              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton
                size="small"
                onClick={handleReset}
                disabled={transitioning || isSubmitting}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <ArrowBack fontSize="small" />
              </IconButton>
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <Typography variant="body2" color="text.secondary">
                  {authMessage}
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={500}
                  fontSize={15}
                  noWrap
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {currentEmail}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Slide>

        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ p: { xs: 3, sm: 4 }, position: 'relative' }}>
            <Grid container spacing={1.5}>
              {!emailChecked && !emailCheckResult && (
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      opacity: emailChecked ? 0.7 : 1,
                      pointerEvents: emailChecked ? 'none' : 'auto',
                    }}
                  >
                    <RHFTextField
                      name="email"
                      label="Adresse email"
                      placeholder="nom@gmail.com"
                      autoComplete="email"
                      fullWidth
                      disabled={checkingEmail || emailChecked}
                      sx={{
                        '& .MuiInputBase-root': {
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          backgroundColor: emailChecked
                            ? alpha(theme.palette.action.disabled, 0.05)
                            : 'transparent',
                        },
                      }}
                    />
                  </Box>
                </Grid>
              )}

              {emailChecked && emailCheckResult && (
                <Fade
                  in={!transitioning}
                  timeout={{ enter: 600, exit: 200 }}
                  style={{ width: '100%' }}
                >
                  <Grid container spacing={1.5}>
                    {shouldShowBlockingMessage() && (
                      <Grid size={{ xs: 12 }}>
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          <Typography variant="body2">
                            {getMessageFromBackend(emailCheckResult.message)}
                          </Typography>
                        </Alert>
                      </Grid>
                    )}

                    {shouldShowPasswordFields() && (
                      <>
                        <Grid size={{ xs: 12 }}>
                          <RHFTextField
                            name="password"
                            label="Mot de passe"
                            type={showPassword ? 'text' : 'password'}
                            autoComplete={isRegistering ? 'new-password' : 'current-password'}
                            fullWidth
                            autoFocus
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="end"
                                    size="small"
                                  >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        {/* Confirm password — only for registration flow */}
                        {isRegistering && (
                          <Grid size={{ xs: 12 }}>
                            <RHFTextField
                              name="confirmPassword"
                              label="Confirmer le mot de passe"
                              type={showConfirmPassword ? 'text' : 'password'}
                              autoComplete="new-password"
                              fullWidth
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <IconButton
                                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                      edge="end"
                                      size="small"
                                    >
                                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                        )}

                        {/* NO "Mot de passe oublié ?" on the signup page */}
                      </>
                    )}
                  </Grid>
                </Fade>
              )}

              {!showSocialOnlyMessage && (
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      opacity: transitioning ? 0.5 : 1,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      transform: transitioning ? 'scale(0.98)' : 'scale(1)',
                    }}
                  >
                    <StyledLoadingButton
                      type="submit"
                      loading={isSubmitting || checkingEmail}
                      disabled={transitioning}
                    >
                      {(() => {
                        if (!emailChecked) {
                          return 'Créer un compte';
                        }
                        if (isRegistering) {
                          return 'Créer mon compte';
                        }
                        return 'Se connecter';
                      })()}
                    </StyledLoadingButton>
                  </Box>
                </Grid>
              )}

              {!shouldShowPasswordFields() && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {socialDividerLabel}
                      </Typography>
                    </Divider>
                  </Grid>
                  {renderSocialButtons()}
                </>
              )}
            </Grid>
          </Box>
        </FormProvider>
      </AuthCard>
      {!shouldShowPasswordFields() && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            variant="text"
            startIcon={<LoginOutlined />}
            onClick={() => router.push({ pathname: Routes.Auth.Login, query: router.query })}
            sx={{
              textTransform: 'none',
              color: 'primary.main',
              '& .MuiButton-startIcon': { color: 'primary.main' },
              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.05) },
            }}
          >
            J'ai déjà un compte
          </Button>
        </Box>
      )}
    </AuthLayout>
  );
};

export default SignupForm;
